import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { KEYUTIL, KJUR, RSAKey } from 'jsrsasign';
import { BehaviorSubject, Observable, of as observableOf, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppConfig } from '../app-config.loader';
import { LoaderExclusionType, LoaderService } from '../layout/loader/loader.service';
import { JwtResponse } from './identity.model';

/**
 * Implementa i metodi per il login con IDeM e la gestione dei token JWT.
 *
 * @export
 * @class IdentityService
 */
@Injectable({
  providedIn: 'root'
})
export class IdentityService {
  public userLogin: string | undefined;
  public userName: string | undefined;
  public sessionId: string | undefined;
  public isLogged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public authenticationAPIRoot: string;

  private exp: number | undefined;

  private claims: Record<string, string | string[]> | undefined;
  private authenticationUrl: string;

  constructor(
    private readonly appConfig: AppConfig,
    private readonly httpClient: HttpClient,
    private readonly jwtHelper: JwtHelperService
  ) {
    LoaderService.addExclusion('authenticate', LoaderExclusionType.isContained);
    const jwt = localStorage.getItem(this.appConfig.settings.jwt.tokenName);

    const url = this.appConfig.settings.jwt.authenticationUrl;
    const index = url.indexOf('/authenticate');
    this.authenticationAPIRoot = url.substring(0, index < 0 ? url.length : index);
    this.authenticationUrl = index === -1 ? `${this.authenticationAPIRoot}/authenticate` : url;
    if (index < 0 && !environment.production) {
      this.authenticationUrl = `${this.authenticationUrl}/${appConfig.settings.applicationName}`;
    }

    if (jwt !== null) {
      this.decodeJWT(jwt);
    }
  }

  /**
   * Effettua il login
   */
  public login(): Observable<boolean> {
    const jwt = localStorage.getItem(this.appConfig.settings.jwt.tokenName);
    if (jwt !== null && this.decodeJWT(jwt)) {
      return observableOf(true);
    }

    return this.httpClient.get<JwtResponse>(this.authenticationUrl, { withCredentials: true }).pipe(
      map((response) => this.decodeJWT(response.token)),
      catchError((err: HttpErrorResponse) => this.loginError(err))
    );
  }

  /**
   * Effettua il login
   */
  public loginWithUsername(username: string, password: string): Observable<boolean> {
    this.logout();
    this.userName = username;
    const loginData = { username, password };
    return this.httpClient.post<JwtResponse>(this.authenticationUrl, loginData).pipe(
      map((response) => this.decodeJWT(response.token)),
      catchError((err: HttpErrorResponse) => this.loginError(err))
    );
  }

  /**
  * Effettua il logout
  */
  public logout(): void {
    localStorage.removeItem(this.appConfig.settings.jwt.tokenName);
    if (this.appConfig.settings.jwt.setCookie) {
      document.cookie = this.appConfig.settings.jwt.tokenName + '=; exipres=Thu, 01 Jan 1970 00:00:01 GMT;';
    }

    delete this.userLogin;
    delete this.userName;
    delete this.sessionId;
    delete this.claims;
    this.isLogged$.next(false);
  }

  /**
   * Verifica che l'utente abbia il claim specificato, opzionalmente con il valore specificato.
   *
   * @param claimName Nome del claim da verificare
   * @param claimValue Valore, opzionale, del claim da verificare
   */
  public hasClaim(claimName: string | undefined, claimValue?: string): boolean {
    if (typeof claimName === 'undefined' || claimName === '') return true;
    if (typeof this.claims === 'undefined') return false;

    const claim = this.claims[claimName];

    // Non ha il claim
    if (typeof claim === 'undefined') return false;

    // Non è stato specificato un valore da controllare
    if (typeof claimValue === 'undefined' || claimValue === null) return true;

    const isArrayString = claim.indexOf(',') > -1;
    // Controllo il valore stringa
    if (typeof claim === 'string' && !isArrayString && claimValue.toUpperCase() === claim.toUpperCase()) return true;

    // Controllo il valore nell'array o nella stringa contenente virgole
    const claimAsArray = typeof claim === 'object' ? claim : claim.split(',');
    if (claimAsArray.findIndex(c => c.toUpperCase() === claimValue.toUpperCase()) > -1) return true;

    return false;
  }

  /**
   * Restituisce il valore del claim specificato
   *
   * @param {string} claimName Il nome del claim
   * @returns Il valore del claim
   * @memberof IdentityService
   */
  public getClaimValue(claimName: string): string | string[] | undefined {
    if (typeof this.claims === 'undefined') return;

    return this.claims[claimName];
  }

  public isExpired() : boolean {

    if (!this.exp)
      return true;

    return ( this.exp < (Date.now() / 1000)); 
  }

  private decodeJWT(jwt: string): boolean {
    if (typeof jwt === 'undefined' || !this.validateToken(jwt)) {
      this.logout();
      return false;
    }

    localStorage.setItem(this.appConfig.settings.jwt.tokenName, jwt);
    if (this.appConfig.settings.jwt.setCookie) {
      document.cookie = this.appConfig.settings.jwt.tokenName + '=' + jwt + ';path=/';
    }

    const token = this.jwtHelper.decodeToken<Record<string, string>>(jwt);
    if (!token) {
      console.error('Cannot decode token');
      this.isLogged$.next(false);
      return false;
    }

    this.userLogin = token.sub;
    this.userName = token['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name'];
    this.sessionId = token.jti;
    this.exp = +token.exp;
    this.claims = token;

    if (this.appConfig.settings.jwt.debug) {
      console.log(token);
    }

    this.isLogged$.next(true);
    return true;
  }

  private validateToken(jwt: string): boolean {
    const pubkey = KEYUTIL.getKey(this.appConfig.settings.jwt.publicKey) as RSAKey;
    try {
      return KJUR.jws.JWS.verifyJWT(jwt, pubkey, {
        alg: ['RS256'],
        iss: [this.appConfig.settings.jwt.issuer],
        gracePeriod: 3600
      });
    } catch (error) {
      console.error(error);
      return false;
    }
  }

  private loginError(error: HttpErrorResponse) {
    this.logout();
    if (error.status === 401) {
      return observableOf(false);
    }

    if (!environment.production && error.statusText) {
      console.error(`Errore invocazione API: ${error.statusText}`);
    }

    return throwError(error);
  }
}
